import React, { useMemo, useState } from "react";
import { Tool } from "../../types/tools";
import { ViewerSettings } from "../../types/settings";
import { t } from "../../utils/i18n";
import { withOpacity } from "../../utils/colorUtils";
interface ToolsPanelProps {
  isOpen?: boolean;
  tools?: Tool[];
  selectedToolId?: string;
  onToolSelect?: (tool: Tool) => void;
  onViewItem?: (tool: Tool, itemType: "tool") => void;
  panelTitle?: string;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  titleColor?: string;
  textColor?: string;
  mutedTextColor?: string;
  iconColor?: string;
  activeColor?: string;
  fontFamily?: string;
  language?: ViewerSettings["language"];
}

const DEFAULT_TOOLS: Tool[] = [
  {
    id: "laser-calibrator",
    name: "Laser Calibrator",
    code: "LC-900-X",
    icon: "biotech",
  },
  {
    id: "torque-wrench",
    name: "Torque Wrench",
    code: "TW-450-D",
    icon: "build",
  },
  {
    id: "digital-multimeter",
    name: "Digital Multimeter",
    code: "DM-PRO",
    icon: "rebase_edit",
  },
  {
    id: "alignment-jig",
    name: "Alignment Jig",
    code: "AJ-V4-SET",
    icon: "grid_view",
  },
  {
    id: "firmware-uploader",
    name: "Firmware Uploader",
    code: "FW-LINK",
    icon: "upload_file",
  },
  {
    id: "thermal-camera",
    name: "Thermal Camera",
    code: "TC-88-IR",
    icon: "thermostat",
  },
  {
    id: "micro-screwdriver",
    name: "Micro-Screwdriver Set",
    code: "MS-PRECISION",
    icon: "hand_package",
  },
  {
    id: "soldering-station",
    name: "Soldering Station",
    code: "SS-400-W",
    icon: "flare",
  },
];

export function ToolsPanel({
  isOpen = false,
  tools = DEFAULT_TOOLS,
  selectedToolId,
  onToolSelect,
  onViewItem,
  panelTitle = "Tools",
  backgroundColor = "rgba(55, 55, 55, 0.85)",
  backdropBlur = "24px",
  borderColor = "rgba(255, 255, 255, 0.12)",
  boxShadow = "0 8px 32px 0 rgba(0, 0, 0, 0.4)",
  titleColor = "rgb(56, 189, 248)",
  textColor = "rgba(255, 255, 255, 0.9)",
  mutedTextColor = "rgba(255, 255, 255, 0.45)",
  iconColor = "rgba(255, 255, 255, 0.75)",
  activeColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
  language = "en",
}: ToolsPanelProps) {
  const [query, setQuery] = useState("");
  const [localSelectedToolId, setLocalSelectedToolId] = useState<
    string | undefined
  >(tools[0]?.id);

  const activeToolId = selectedToolId ?? localSelectedToolId;

  const filteredTools = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return tools;

    return tools.filter(
      (tool) =>
        tool.name.toLowerCase().includes(normalizedQuery) ||
        tool.code.toLowerCase().includes(normalizedQuery),
    );
  }, [tools, query]);

  if (!isOpen) return null;

  return (
    <aside
      style={{
        position: "absolute",
        left: "1.5rem",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 100,
        width: "18rem",
        height: "calc(100% - 12rem)",
        maxHeight: "700px",
      }}
      data-oid="xz34vhs"
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow,
          borderRadius: "1.5rem",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          fontFamily,
        }}
        data-oid="44p-v9o"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          data-oid="g6vy9-m"
        >
          <h2
            style={{
              margin: 0,
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: `${titleColor}CC`,
            }}
            data-oid="lj7kh_c"
          >
            {panelTitle === "Tools" ? t(language, "panel.tools") : panelTitle}
          </h2>
          <span
            style={{
              fontSize: "0.625rem",
              padding: "0.25rem 0.5rem",
              borderRadius: "0.25rem",
              color: mutedTextColor,
              backgroundColor: "rgba(255, 255, 255, 0.05)",
            }}
            data-oid="jssc3it"
          >
            {filteredTools.length} {t(language, "panel.items")}
          </span>
        </div>

        <div style={{ position: "relative" }} data-oid="wmmldxs">
          <span
            className="material-symbols-outlined"
            style={{
              position: "absolute",
              left: "0.75rem",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "0.875rem",
              color: mutedTextColor,
              fontWeight: 300,
              pointerEvents: "none",
            }}
            data-oid="c99d9fh"
          >
            search
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t(language, "panel.searchTools")}
            style={{
              width: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "0.75rem",
              padding: "0.5rem 1rem 0.5rem 2.25rem",
              fontSize: "0.75rem",
              color: textColor,
              outline: "none",
              transition: "all 0.2s ease",
            }}
            onFocus={(e) => {
              e.currentTarget.style.border =
                `1px solid ${withOpacity(activeColor, 0.5)}`;
              e.currentTarget.style.boxShadow =
                `0 0 0 1px ${withOpacity(activeColor, 0.2)}`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.border =
                "1px solid rgba(255, 255, 255, 0.1)";
              e.currentTarget.style.boxShadow = "none";
            }}
            data-oid="8nwg.h2"
          />
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            paddingRight: "0.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255, 255, 255, 0.2) transparent",
          }}
          data-oid="3zcmvs_"
        >
          {filteredTools.map((tool) => {
            const isActive = tool.id === activeToolId;
            return (
              <button
                key={tool.id}
                onClick={() => {
                  setLocalSelectedToolId(tool.id);
                  onToolSelect?.(tool);
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                  padding: "0.75rem",
                  borderRadius: "0.75rem",
                  cursor: "pointer",
                  border: isActive
                    ? `1px solid ${activeColor}4D`
                    : "1px solid transparent",
                  background: isActive ? `${activeColor}1A` : "transparent",
                  color: isActive ? activeColor : textColor,
                  transition: "all 0.2s",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.05)";
                    e.currentTarget.style.border =
                      "1px solid rgba(255, 255, 255, 0.1)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.border = "1px solid transparent";
                  }
                }}
                data-oid="y0rgeln"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    flex: 1,
                    minWidth: 0,
                  }}
                  data-oid="y7wabd9"
                >
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: 300,
                      color: isActive ? activeColor : iconColor,
                      flexShrink: 0,
                    }}
                    data-oid=":yuu4.q"
                  >
                    {tool.icon}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 0,
                    }}
                    data-oid=".ls9:7f"
                  >
                    <span
                      style={{
                        fontSize: "0.8125rem",
                        fontWeight: 500,
                        color: isActive ? activeColor : textColor,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      data-oid="7r3r229"
                    >
                      {tool.name}
                    </span>
                    <span
                      style={{
                        fontSize: "0.625rem",
                        color: isActive ? `${activeColor}99` : mutedTextColor,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      data-oid="u60m8j."
                    >
                      {tool.code}
                    </span>
                  </div>
                </div>

                {/* View Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewItem?.(tool, "tool");
                  }}
                  className="viewButton"
                  style={{
                    width: "32px",
                    height: "32px",
                    background: "transparent",
                    color: mutedTextColor,
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      withOpacity(activeColor, 0.15);
                    e.currentTarget.style.color = activeColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = mutedTextColor;
                  }}
                  aria-label={`${t(language, "panel.viewDetailsFor")} ${tool.name}`}
                  data-oid="t1yx0_h"
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "1.25rem", fontWeight: 300 }}
                    data-oid="d1k74n4"
                  >
                    visibility
                  </span>
                </button>
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
